<template>
  <div class="snow-overlay" aria-hidden="true" :style="{ opacity: overlayOpacity.toString() }">
    <span
      v-for="flake in visibleFlakes"
      :key="flake.id"
      class="snowflake"
      :style="flake.style"
    >
      ❄
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

type SnowflakeStyle = {
  left: string;
  fontSize: string;
  animationDuration: string;
  animationDelay: string;
  opacity: string;
};

type Snowflake = {
  id: number;
  style: SnowflakeStyle;
};

const INITIAL_FLAKE_COUNT = 12;
const DURATION_MS: number = 2 * 60 * 1000;
const TICK_MS = 1000;

const flakes = ref<Snowflake[]>(
  Array.from({ length: INITIAL_FLAKE_COUNT }, (_, index: number): Snowflake => {
    const size: number = 8 + Math.random() * 8;
    const duration: number = 14 + Math.random() * 8;
    const delay: number = Math.random() * 10;
    const left: number = Math.random() * 100;
    const opacity: number = 0.15 + Math.random() * 0.2;

    return {
      id: index,
      style: {
        left: `${left}%`,
        fontSize: `${size}px`,
        animationDuration: `${duration}s`,
        animationDelay: `-${delay}s`,
        opacity: `${opacity}`
      }
    };
  })
);

const elapsedMs = ref<number>(0);
const visibleCount = ref<number>(INITIAL_FLAKE_COUNT);

let intervalId: number | null = null;

const progress = computed<number>(() => {
  const ratio: number = elapsedMs.value / DURATION_MS;

  return Math.min(ratio, 1);
});

const overlayOpacity = computed<number>(() => 1 - progress.value);

const visibleFlakes = computed<Snowflake[]>((): Snowflake[] =>
  flakes.value.slice(0, visibleCount.value)
);

onMounted((): void => {
  intervalId = window.setInterval((): void => {
    elapsedMs.value = Math.min(elapsedMs.value + TICK_MS, DURATION_MS);

    const nextCount: number = Math.round(INITIAL_FLAKE_COUNT * (1 - progress.value));
    visibleCount.value = Math.max(nextCount, 0);

    if (elapsedMs.value >= DURATION_MS && intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  }, TICK_MS);
});

onBeforeUnmount((): void => {
  if (intervalId !== null) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
});
</script>

<style scoped>
.snow-overlay {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 9999;
  transition: opacity 1s linear;
}
.snowflake {
  position: absolute;
  top: -10%;
  line-height: 1;
  user-select: none;
  will-change: transform;
  filter: blur(0.3px);
  animation-name: snowfall;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

@keyframes snowfall {
  0% {
    transform: translate3d(0, -10vh, 0);
  }

  25% {
    transform: translate3d(4px, 25vh, 0);
  }

  50% {
    transform: translate3d(-3px, 50vh, 0);
  }

  75% {
    transform: translate3d(5px, 75vh, 0);
  }

  100% {
    transform: translate3d(-2px, 110vh, 0);
  }
}
</style>
